import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Globe,
  CreditCard,
  Coins,
  Plus,
  Trash2,
  Lock,
  Unlock,
  Loader2,
  Mail,
  Shield,
  UserCheck,
  RefreshCw,
  XCircle,
  Link2,
  Check,
} from "lucide-react";
import * as Sentry from "@sentry/react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { parseEdgeFunctionError } from "@/lib/parseEdgeFunctionError";

interface Member {
  id: string;
  user_id: string;
  email: string;
  role: string;
  status: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  email_sent_at: string | null;
  email_error: string | null;
  token: string;
}

export default function WorkspaceSettings() {
  const { workspace, isAdmin, unlockedRegions, allRegions, refreshWorkspace } = useWorkspace();

  // AI-transparency: workspace default for the visible export watermark.
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [savingWatermark, setSavingWatermark] = useState(false);
  useEffect(() => {
    setWatermarkEnabled(workspace?.ai_watermark_enabled ?? true);
  }, [workspace?.ai_watermark_enabled]);

  const handleToggleWatermark = async (next: boolean) => {
    if (!workspace) return;
    const previous = watermarkEnabled;
    setWatermarkEnabled(next); // optimistic
    setSavingWatermark(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ ai_watermark_enabled: next } as any)
        .eq("id", workspace.id);
      if (error) throw error;
      await refreshWorkspace();
    } catch (err) {
      console.error("Failed to update AI watermark setting:", err);
      Sentry.captureException(err);
      setWatermarkEnabled(previous); // roll back
    } finally {
      setSavingWatermark(false);
    }
  };
  const { user } = useAuth();
  const { data: creditBalance, isLoading: creditsLoading, isError: creditsError } = useCreditBalance(workspace?.id);
  const creditsResolved = !creditsLoading && !creditsError && creditBalance != null;

  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ url?: string; error?: string; emailSent?: boolean; emailError?: string | null; recipient?: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    | { type: "remove_member"; membershipId: string; email: string }
    | { type: "revoke_invite"; invitationId: string; email: string }
    | null
  >(null);

  async function copyInviteLink(url: string, id: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 1600);
    } catch (err) {
      console.error("Clipboard write failed:", err);
      setCopiedId(`failed:${id}`);
      window.setTimeout(() => setCopiedId((curr) => (curr === `failed:${id}` ? null : curr)), 2600);
    }
  }

  const inviteUrlFor = (token: string) => `${window.location.origin}/invite/${token}`;
  const [membersError, setMembersError] = useState<string | null>(null);

  useEffect(() => {
    if (workspace) {
      loadMembers();
      loadPendingInvitations();
    } else {
      setLoading(false);
    }
  }, [workspace]);

  const loadMembers = async () => {
    if (!workspace) return;
    setLoading(true);
    setMembersError(null);

    const { data: memberships, error: membershipsErr } = await supabase
      .from("workspace_memberships")
      .select("id, user_id, role, status")
      .eq("workspace_id", workspace.id)
      .eq("is_deleted", false)
      .neq("status", "removed");

    if (membershipsErr) {
      console.error("Failed to load members:", membershipsErr);
      Sentry.captureException(membershipsErr);
      setMembersError("Couldn't load team members. Refresh to try again.");
      setLoading(false);
      return;
    }

    const userIds = (memberships ?? []).map((m) => m.user_id);
    const { data: profiles, error: profilesErr } = userIds.length
      ? await supabase.from("profiles").select("user_id, email").in("user_id", userIds)
      : { data: [], error: null };

    if (profilesErr) {
      console.error("Failed to load profiles:", profilesErr);
      Sentry.captureException(profilesErr);
      // Non-fatal: render members without emails rather than showing nothing.
    }

    const emailMap = new Map(profiles?.map((p) => [p.user_id, p.email || ""]) || []);

    setMembers(
      (memberships ?? []).map((m) => ({
        ...m,
        email: emailMap.get(m.user_id) || "Unknown",
      }))
    );

    setLoading(false);
  };

  const loadPendingInvitations = async () => {
    if (!workspace) return;
    const { data, error } = await supabase
      .from("invitations")
      .select("id, email, role, expires_at, email_sent_at, email_error, token")
      .eq("workspace_id", workspace.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to load pending invitations:", error);
      Sentry.captureException(error);
      setMembersError("Couldn't load pending invitations. Refresh to try again.");
      return;
    }
    setPendingInvitations(data || []);
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim() || !workspace || !isAdmin) return;
    setInviting(true);
    setInviteResult(null);

    const { data, error } = await supabase.functions.invoke("send-invitation", {
      body: { workspace_id: workspace.id, email: inviteEmail.trim(), role: "member" },
    });

    setInviting(false);

    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Failed to send invitation";
      console.error("send-invitation error:", error, "parsed:", body);
      Sentry.captureException(error);
      setInviteResult({ error: errMsg });
      return;
    }

    setInviteResult({
      url: data.invite_url,
      emailSent: data.email_sent,
      emailError: data.email_error,
      recipient: inviteEmail.trim(),
    });
    setInviteEmail("");
    loadPendingInvitations();
  };

  const resendInvite = async (invitationId: string) => {
    if (!workspace || !isAdmin) return;
    const inv = pendingInvitations.find((i) => i.id === invitationId);
    if (!inv) return;

    const { data, error } = await supabase.functions.invoke("send-invitation", {
      body: { workspace_id: workspace.id, email: inv.email, role: inv.role },
    });

    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Failed to resend invitation";
      console.error("Resend failed:", error, "parsed:", body);
      Sentry.captureException(error);
      setInviteResult({ error: errMsg });
      return;
    }

    setInviteResult({
      url: data.invite_url,
      emailSent: data.email_sent,
      emailError: data.email_error,
      recipient: inv.email,
    });
    loadPendingInvitations();
  };

  const requestRevokeInvite = (invitationId: string, email: string) => {
    if (!isAdmin) return;
    setConfirmAction({ type: "revoke_invite", invitationId, email });
  };

  const revokeInvite = async (invitationId: string) => {
    if (!isAdmin) return;
    const { error } = await supabase.functions.invoke("revoke-invitation", {
      body: { invitation_id: invitationId },
    });

    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Failed to revoke invitation";
      console.error("Revoke failed:", error, "parsed:", body);
      Sentry.captureException(error);
      setInviteResult({ error: errMsg });
      return;
    }
    loadPendingInvitations();
  };

  const requestRemoveMember = (membershipId: string, email: string) => {
    if (!isAdmin) return;
    setConfirmAction({ type: "remove_member", membershipId, email });
  };

  const removeMember = async (membershipId: string) => {
    if (!isAdmin) return;
    setMembersError(null);
    const { error } = await supabase.functions.invoke("remove-member", {
      body: { membership_id: membershipId },
    });
    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Couldn't remove member";
      console.error("Remove member failed:", error, "parsed:", body);
      Sentry.captureException(error);
      setMembersError(errMsg);
      return;
    }
    loadMembers();
  };

  const updateRole = async (membershipId: string, newRole: string) => {
    if (!isAdmin) return;
    setMembersError(null);

    const previousMembers = members;
    setMembers((curr) => curr.map((m) => (m.id === membershipId ? { ...m, role: newRole } : m)));

    const { error } = await supabase.functions.invoke("update-member-role", {
      body: { membership_id: membershipId, role: newRole },
    });
    if (error) {
      setMembers(previousMembers);
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Couldn't change role";
      console.error("Update role failed:", error, "parsed:", body);
      Sentry.captureException(error);
      setMembersError(errMsg);
      return;
    }
    loadMembers();
  };

  const unlockedKeys = new Set(unlockedRegions.map((r) => r.region_key));

  // Group regions by bundle
  const regionBundles = allRegions.reduce<Record<string, typeof allRegions>>((acc, r) => {
    const key = r.bundle_key || "other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
      <h1 className="font-display text-2xl font-bold mb-6">Workspace Settings</h1>

      <Tabs defaultValue="billing">
        <TabsList data-tour="settings.tabs" className="mb-6">
          <TabsTrigger value="billing">Plan & Billing</TabsTrigger>
          <TabsTrigger value="members">Seats & Members</TabsTrigger>
          <TabsTrigger value="regions">Regions</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        {/* Plan & Billing */}
        <TabsContent value="billing">
          <div className="glass rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-lg">Current Plan</h3>
                <p className="text-muted-foreground text-sm capitalize">{workspace?.subscription_tier || "Free"} Plan</p>
              </div>
              {isAdmin && (
                <Button variant="outline" disabled>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage (Coming Soon)
                </Button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Coins className="w-4 h-4" />
                  <span className="text-xs font-medium">Credits Remaining</span>
                </div>
                <p className="font-display text-2xl font-bold">
                  {creditsResolved ? creditBalance.toLocaleString() : "—"}
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-xs font-medium">Seats</span>
                </div>
                <p className="font-display text-2xl font-bold">
                  {members.length} / {workspace?.seat_limit || 2}
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-medium">Regions</span>
                </div>
                <p className="font-display text-2xl font-bold">
                  {unlockedRegions.length}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Members */}
        <TabsContent value="members">
          <div className="glass rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-lg">Team Members</h3>
                <p className="text-muted-foreground text-sm">
                  {members.length} of {workspace?.seat_limit || 2} seats used
                </p>
              </div>
            </div>

            {membersError && (
              <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                {membersError}
              </div>
            )}

            {/* Invite */}
            {isAdmin && (
              <div data-tour="settings.invite" className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Email address"
                    className="bg-secondary border-border"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && inviteMember()}
                    disabled={inviting}
                  />
                  <Button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()}>
                    {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    Invite
                  </Button>
                </div>
                {inviteResult?.url && inviteResult.emailSent && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md space-y-2">
                    <p className="text-xs text-green-700 dark:text-green-400">
                      Invitation email sent to <strong>{inviteResult.recipient}</strong>.
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs break-all select-all bg-background/60 border border-green-200 dark:border-green-800 rounded px-2 py-1">
                        {inviteResult.url}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 shrink-0"
                        onClick={() => copyInviteLink(inviteResult.url!, "result")}
                      >
                        {copiedId === "result" ? (
                          <><Check className="w-3 h-3 mr-1" />Copied</>
                        ) : copiedId === "failed:result" ? (
                          <><XCircle className="w-3 h-3 mr-1" />Copy failed</>
                        ) : (
                          <><Link2 className="w-3 h-3 mr-1" />Copy link</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                {inviteResult?.url && inviteResult.emailSent === false && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md space-y-2">
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      Invitation created, but the email didn't send
                      {inviteResult.emailError ? `: ${inviteResult.emailError}` : "."}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">Share this link manually:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs break-all select-all bg-background/60 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
                        {inviteResult.url}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 shrink-0"
                        onClick={() => copyInviteLink(inviteResult.url!, "result")}
                      >
                        {copiedId === "result" ? (
                          <><Check className="w-3 h-3 mr-1" />Copied</>
                        ) : copiedId === "failed:result" ? (
                          <><XCircle className="w-3 h-3 mr-1" />Copy failed</>
                        ) : (
                          <><Link2 className="w-3 h-3 mr-1" />Copy link</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                {inviteResult?.error && (
                  <p className="text-sm text-destructive">{inviteResult.error}</p>
                )}
              </div>
            )}

            {/* Pending invitations */}
            {isAdmin && pendingInvitations.length > 0 && (
              <div className="pt-2">
                <h4 className="text-sm font-semibold mb-2">Pending Invitations</h4>
                <div className="divide-y divide-border border border-border rounded-md">
                  {pendingInvitations.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm truncate">{inv.email}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {inv.role} • expires {new Date(inv.expires_at).toLocaleDateString()}
                          {inv.email_sent_at ? (
                            <span className="normal-case"> • email sent {new Date(inv.email_sent_at).toLocaleDateString()}</span>
                          ) : inv.email_error ? (
                            <span className="normal-case text-amber-700 dark:text-amber-400"> • email failed</span>
                          ) : (
                            <span className="normal-case"> • email pending</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8"
                              onClick={() => copyInviteLink(inviteUrlFor(inv.token), inv.id)}
                              aria-label="Copy invite link"
                            >
                              {copiedId === inv.id ? (
                                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                              ) : copiedId === `failed:${inv.id}` ? (
                                <XCircle className="w-4 h-4 text-destructive" />
                              ) : (
                                <Link2 className="w-4 h-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {copiedId === inv.id
                              ? "Copied!"
                              : copiedId === `failed:${inv.id}`
                              ? "Copy failed — select the URL and copy manually"
                              : "Copy invite link"}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8"
                              onClick={() => resendInvite(inv.id)}
                              aria-label="Resend invitation email"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Resend email (generates a new link and revokes the old one)</TooltipContent>
                        </Tooltip>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 text-destructive"
                              onClick={() => requestRevokeInvite(inv.id, inv.email)}
                              aria-label="Revoke invitation"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Revoke invitation</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Member list */}
            <div className="divide-y divide-border">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                      {member.role === "admin" ? (
                        <Shield className="w-4 h-4 text-primary" />
                      ) : (
                        <UserCheck className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{member.email}</p>
                      <p className="text-xs text-muted-foreground capitalize">{member.role} • {member.status}</p>
                    </div>
                  </div>
                  {isAdmin && member.user_id !== user?.id && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={member.role}
                        onValueChange={(v) => updateRole(member.id, v)}
                      >
                        <SelectTrigger className="w-[110px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                        </SelectContent>
                      </Select>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 text-destructive"
                            onClick={() => requestRemoveMember(member.id, member.email)}
                            aria-label="Remove from workspace"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Remove from workspace</TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Regions */}
        <TabsContent value="regions">
          <div className="glass rounded-xl p-6 space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg">Regions</h3>
              <p className="text-muted-foreground text-sm">
                {unlockedRegions.length} of {allRegions.length} regions unlocked
              </p>
            </div>

            {Object.entries(regionBundles).map(([bundle, regions]) => (
              <div key={bundle}>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {bundle}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {regions.map((region) => {
                    const isUnlocked = unlockedKeys.has(region.region_key);
                    return (
                      <div
                        key={region.region_key}
                        className={cn(
                          "flex items-center justify-between px-4 py-3 rounded-lg",
                          isUnlocked ? "bg-primary/10 border border-primary/20" : "bg-secondary"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {isUnlocked ? (
                            <Unlock className="w-4 h-4 text-primary" />
                          ) : (
                            <Lock className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className={cn("text-sm", !isUnlocked && "text-muted-foreground")}>
                            {region.display_name}
                          </span>
                        </div>
                        {!isUnlocked && isAdmin && (
                          <Button variant="ghost" size="sm" className="text-xs h-7" disabled>
                            Unlock
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="compliance">
          <div className="glass rounded-xl p-6 space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg">AI transparency</h3>
              <p className="text-muted-foreground text-sm">
                How generated assets are marked as AI-created (EU AI Act, Article 50)
              </p>
            </div>

            <div className="flex items-start justify-between gap-6 px-4 py-3 rounded-lg bg-secondary">
              <div className="space-y-1">
                <p className="text-sm font-medium">Visible watermark on exports</p>
                <p className="text-xs text-muted-foreground max-w-lg">
                  Burns a subtle “AI generated” label into the bottom-right corner of
                  downloaded images. Supports the disclosure duty for publishing
                  photorealistic synthetic imagery. Can be overridden per export.
                </p>
              </div>
              <Switch
                checked={watermarkEnabled}
                onCheckedChange={handleToggleWatermark}
                disabled={!isAdmin || savingWatermark}
              />
            </div>

            <div className="flex items-start justify-between gap-6 px-4 py-3 rounded-lg bg-secondary/50">
              <div className="space-y-1">
                <p className="text-sm font-medium">Machine-readable provenance</p>
                <p className="text-xs text-muted-foreground max-w-lg">
                  Every exported image carries embedded IPTC metadata marking it as
                  AI-generated (<code className="text-[10px]">DigitalSourceType</code>). This
                  is the marking required of providers under Article 50(2) and is always
                  applied — it cannot be switched off.
                </p>
              </div>
              <span className="text-xs font-medium text-primary whitespace-nowrap">Always on</span>
            </div>

            {!isAdmin && (
              <p className="text-xs text-muted-foreground">
                Only workspace admins can change these settings.
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "remove_member" && "Remove from workspace?"}
              {confirmAction?.type === "revoke_invite" && "Revoke invitation?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "remove_member" && (
                <>
                  <strong>{confirmAction.email}</strong> will lose access to this workspace. Their account and any content they created stay intact, and they'll still have access to any other workspaces they're a member of. You can re-invite them later.
                </>
              )}
              {confirmAction?.type === "revoke_invite" && (
                <>
                  The invitation sent to <strong>{confirmAction.email}</strong> will stop working. If they try the link, they'll see "Invalid invitation". You can send a fresh invite later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "remove_member") removeMember(confirmAction.membershipId);
                if (confirmAction.type === "revoke_invite") revokeInvite(confirmAction.invitationId);
                setConfirmAction(null);
              }}
            >
              {confirmAction?.type === "remove_member" ? "Remove" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
